import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { DealActions, ExpenseActions, ExpenseTypeActions } from '../../../../actions';
import Button from '../../../../components/ui/Button/Button';
import ContainerCard from '../../../../components/ui/ContainerCard/ContainerCard';
import Error from '../../../../components/ui/Error/Error';
import CheckboxInput from '../../../../components/ui/Inputs/CheckboxInput/CheckboxInput';
import DateInput from '../../../../components/ui/Inputs/DateInput/DateInput';
import SelectInput from '../../../../components/ui/Inputs/SelectInput/SelectInput';
import TextAreaInput from '../../../../components/ui/Inputs/TextAreaInput';
import TextInput from '../../../../components/ui/Inputs/TextInput';
import { notify } from '../../../../components/ui/Toast/Toast';
import { fetchSelectData, frenchFormat, getErrors, standardFormat } from '../../../../utils';

function CreateNewExpense({userData, expenseToBeUpdated, setSwitchForm, switchForm, setView, refreshData}) {
    // -- states --
    const [disabled, setDisabled] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState(null);
    const [deals, setDeals] = useState(null);
    const [types, setTypes] = useState(null);

    const vat = useSelector((state) => state.GlobalsReducer.vat);

    // Check date value for new expense from calendar popup
    const defaultDate = (expenseToBeUpdated && expenseToBeUpdated.date && !expenseToBeUpdated.id) ? dayjs(expenseToBeUpdated.date) : dayjs();

    // -- react hook form --
    const defaultValues = {
        date: defaultDate,
        billable: false,
        vat: vat,
        deal: '',
        amount: null,
        co2: null,
        type: '',
        notes: ''
    };
    const {handleSubmit, control, reset, watch , formState: {isValid}} = useForm({defaultValues});

    useEffect(() => {
        const type = watch('type');

        setSwitchForm(type);
    }, [watch('type')]);

    // -- fill expense values in form for update --
    useEffect(() => {
        if (expenseToBeUpdated && expenseToBeUpdated.id) {
            reset({
                date: dayjs(expenseToBeUpdated.date),
                billable: expenseToBeUpdated.billable,
                vat: (+expenseToBeUpdated.vat).toString(),
                deal: expenseToBeUpdated.deal,
                amount: (+expenseToBeUpdated.amount).toString(),
                co2: (+expenseToBeUpdated.co2).toString(),
                type: expenseToBeUpdated.type,
                notes: expenseToBeUpdated.notes
            });
        }
        else {
            reset({
                type: switchForm,
                date: defaultDate,
                billable: false,
                vat: vat,
                amount: null,
                co2: null,
                notes: ''
            });
        }
    }, [expenseToBeUpdated]);

    // -- functions that get data for Select Inputs --
    const handleGetExpenseTypes = async (search, page = 1) => {
        await fetchSelectData({action: ExpenseTypeActions.getAllExpenseTypes, search:`&search=${search}`, page: page, setter: setTypes, userData, resultPath: 'data'});
    };

    const handleGetDeals = async (search, page) => {
        await fetchSelectData({action: DealActions.getAllDeals, search:`&search=${search}&display=${userData.userProfileName}&include_internal=true`, page: page, setter: setDeals, userData, resultPath: 'data'});
    };

    useEffect(() => {
        // ↓ deals dropdown values
        (async () => {
            await handleGetDeals('', 1);
        })();

        // ↓ expense types dropdown values
        (async () => {
            await handleGetExpenseTypes('', 1);
        })();
    }, []);

    // Fonction de formatage du label des affaires
    // Affiche seulement short_name pour les affaires internes (type activity ou project)
    const formatDealLabel = (deal) => {
        const isInternal = deal.type === 'activity' || deal.type === 'project';
        if (isInternal) {
            return deal.short_name;
        }
        return `${deal.customer?.name} - ${deal.short_name}`;
    };

    // -- handle submit --
    const onSubmit = async (data) => {
        setErrors(null);
        setDisabled(true);
        setLoading(true);

        // ↓ body to send to the post/patch request (see API documentation)
        const expense = {
            deal: {id: data.deal.id, short_name: data.deal.short_name},
            deal_id: data.deal.id,
            type: data.type,
            type_name: data.type.name,
            date: standardFormat(data.date),
            billable: data.billable,
            amount: data.amount,
            vat: data.vat,
            notes: data.notes,
            report: expenseToBeUpdated ? expenseToBeUpdated.report : null
        }

        if (data.co2) {
            expense.co2 = data.co2;
        }

        // ⓘ if it is a new expense, the «report» key is assigned in «createExpense.ts» file

        // ↓ post - new expense
        if (!expenseToBeUpdated || expenseToBeUpdated.date && Object.keys(expenseToBeUpdated).length === 1) {
            const result = await ExpenseActions.createExpense(userData, expense);

            if (!result.success) {
                setErrors(getErrors(result));
            }
            else {
                await refreshData();
                notify('Votre frais à bien été créé');
                setView(false);
            }

            setLoading(false);
            setDisabled(false);
        }
        // ↓ patch - update kilometric expense
        else {
            const result = await ExpenseActions.updateExpense(userData, expenseToBeUpdated.id, expense);

            if (!result.success) {
                setErrors(getErrors(result));
            }
            else {
                await refreshData();
                notify('Votre frais à bien été modifié');
                setView(false);
            }

            setLoading(false);
            setDisabled(false);
        }
    };

    return (
        <ContainerCard
            title={expenseToBeUpdated?.id ? 'Éditer un frais' : 'Nouveau frais'}
            coloredButtonDisplayed={false}
            overflow=''
        >
            <div className="px-4 pt-2">
                <Controller
                    name="deal"
                    control={control}
                    rules={{required: {
                        value: true,
                        message: 'Veuillez sélectionner une affaire.'
                    }}}
                    render={({
                        field: {onChange, value, ref},
                        fieldState: {error}
                    }) => (
                        <SelectInput
                            label='Affaire'
                            placeholder='Affaire'
                            inputRef={ref}
                            value={value}
                            error={error}
                            options={deals?.options || []}
                            hasNextPage={deals?.hasNextPage || false}
                            fetchFunction={handleGetDeals}
                            loading={!deals}
                            onChange={onChange}
                            labelKeys={['customer.name', 'short_name']}
                            labelFormatter={formatDealLabel}
                            isSearchable={true}
                            required={true}
                            disabled={disabled}
                            width="w-full"
                            labelWidth="w-[170px]"
                            separator=' - '
                        />
                    )}
                />
            </div>
            <div className="flex flex-row justify-between px-4 pt-2 pb-4">
                <div className='flex flex-col justify-between'>
                    <div className="mb-2">
                        <Controller
                            name="type"
                            control={control}
                            rules={{required: {
                                value: true,
                                message: 'Veuillez sélectionner un type de frais.'
                            }}}
                            render={({
                                field: {onChange, value, ref},
                                fieldState: {error}
                            }) => (
                                <SelectInput
                                    label='Type de frais'
                                    placeholder='Type de frais'
                                    inputRef={ref}
                                    value={value}
                                    error={error}
                                    options={types?.options || []}
                                    hasNextPage={types?.hasNextPage || false}
                                    fetchFunction={handleGetExpenseTypes}
                                    loading={!types}
                                    onChange={onChange}
                                    labelKeys={['name']}
                                    isSearchable={true}
                                    required={true}
                                    disabled={disabled}
                                />
                            )}
                        />
                    </div>
                    <div className="mb-2">
                        <Controller
                            name="date"
                            control={control}
                            rules={{required: {
                                value: true,
                                message: 'Veuillez sélectionner une date.'
                            }}}
                            render={({
                                field: {onChange, value},
                                fieldState: {error}
                            }) => (
                                <DateInput
                                    label={'Date'}
                                    onChange={onChange}
                                    value={frenchFormat(value)}
                                    error={error}
                                    required={true}
                                    disabled={disabled}
                                />
                            )}
                        />
                    </div>
                    <div className="mb-2 w-fit pl-36">
                        <Controller
                            name="billable"
                            control={control}
                            render={({
                                field: {onChange, value}
                            }) => (
                                <CheckboxInput
                                    uniqueName="billable"
                                    label={'Refacturable'}
                                    onChange={onChange}
                                    value={value}
                                    defaultChecked={value}
                                />
                            )}
                        />
                    </div>
                </div>
                <div className="flex flex-col">
                    <div className="mb-2">
                        <Controller
                            name="vat"
                            control={control}
                            rules={{required: {
                                value: true,
                                message: 'Veuillez saisir la TVA.'
                            }}}
                            render={({
                                field: {onChange, value},
                                fieldState: {error}
                            }) => (
                                <TextInput
                                    label={'TVA récupérable [€]'}
                                    onChange={onChange}
                                    value={value}
                                    error={error}
                                    required={true}
                                    disabled={disabled}
                                    placeholder="TVA"
                                    type="number"
                                />
                            )}
                        />
                    </div>
                    <div className="mb-2">
                        <Controller
                            name="amount"
                            control={control}
                            rules={{required: {
                                value: true,
                                message: 'Veuillez saisir un montant.'
                            }}}
                            render={({
                                field: {onChange, value},
                                fieldState: {error}
                            }) => (
                                <TextInput
                                    label={'Montant TTC [€]'}
                                    onChange={onChange}
                                    value={value}
                                    error={error}
                                    required={true}
                                    disabled={disabled}
                                    placeholder="Montant TTC"
                                    type="number"
                                />
                            )}
                        />
                    </div>
                    <div className="mb-2">
                        <Controller
                            name="co2"
                            control={control}
                            render={({
                                field: {onChange, value},
                                fieldState: {error}
                            }) => (
                                <TextInput
                                    label={'Qté CO2 (g)'}
                                    onChange={onChange}
                                    value={value}
                                    error={error}
                                    required={false}
                                    disabled={disabled}
                                    placeholder="Qté CO2 (g)"
                                    type="number"
                                />
                            )}
                        />
                    </div>
                    {/* <div className="mb-2">
                        <Controller
                            name="type"
                            control={control}
                            rules={{required: {
                                value: true,
                                message: 'Veuillez sélectionner un type de frais.'
                            }}}
                            render={({
                                field: {onChange, value, ref},
                                fieldState: {error}
                            }) => (
                                <SelectInput
                                    label='Type de frais'
                                    placeholder='Type de frais'
                                    inputRef={ref}
                                    value={value}
                                    error={error}
                                    options={types?.options || []}
                                    hasNextPage={types?.hasNextPage || false}
                                    fetchFunction={handleGetExpenseTypes}
                                    loading={!types}
                                    onChange={onChange}
                                    labelKeys={['name']}
                                    isSearchable={true}
                                    required={true}
                                    disabled={disabled}
                                />
                            )}
                        />
                    </div> */}
                </div>
            </div>
            <div className="py-4 mx-4 border-t border-borderGrey">
                <Controller
                    name="notes"
                    control={control}
                    render={({
                        field: {onChange, value}
                    }) => (
                        <TextAreaInput
                            placeholder={'Tapez votre texte'}
                            maxLength={100}
                            onChange={onChange}
                            value={value}
                            disabled={disabled}
                        />
                    )}
                />
            </div>
            <div className="flex flex-col justify-center pb-2">
                <div className="flex justify-center pb-2">
                    <Button
                        onClick={() => setView(false)}
                        type={'secondary'}
                        content={'Annuler'}
                        disabled={disabled}
                    />
                    <Button
                        type={'primary'}
                        content={expenseToBeUpdated?.id ? 'Modifier le frais' : 'Ajouter le frais'}
                        onClick={handleSubmit(onSubmit)}
                        loading={loading}
                        disabled={!deals || !types || !isValid}
                    />
                </div>
                <Error errors={errors}/>
            </div>
        </ContainerCard>
    );
}

export default CreateNewExpense;
